/**
 * Admin MCQ manager — searchable table + create/edit modal with
 *   - manual form
 *   - "Draft with AI" button (paste topic → GPT-4 drafts question)
 *   - image upload OR AI-generate (Nano Banana) + auto-audit
 *   - bulk CSV import
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { api } from "@/App";
import { toast } from "sonner";
import { Search, Plus, Edit2, Trash2, Sparkles, Image as ImageIcon, Upload, X, BadgeCheck, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

const blankMCQ = {
  subject_id: "oral_medicine",
  difficulty: 3,
  clinical_vignette: "",
  question_text: "",
  options: [
    { id: "a", text: "" },
    { id: "b", text: "" },
    { id: "c", text: "" },
    { id: "d", text: "" },
  ],
  correct_option_id: "a",
  explanation: "",
  tags: [],
  status: "published",
};

const AdminMcqsPage = () => {
  const [mcqs, setMcqs] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [hasImage, setHasImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // mcq or "new"
  const [subjects, setSubjects] = useState([]);
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (q) params.set("q", q);
      if (subjectFilter) params.set("subject_id", subjectFilter);
      if (hasImage) params.set("has_image", hasImage);
      const data = await api.get(`/admin/mcqs?${params.toString()}`);
      setMcqs(data.mcqs);
      setTotal(data.total);
    } catch { toast.error("Could not load MCQs"); }
    finally { setLoading(false); }
  }, [q, subjectFilter, hasImage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/subjects").then((d) => setSubjects(d.subjects)); }, []);

  const handleDelete = async (mcq) => {
    if (!window.confirm("Delete this MCQ permanently? This also removes its image file.")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/mcqs/${mcq.id}`, { withCredentials: true });
      toast.success("MCQ deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-mcqs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">MCQs</h2>
          <p className="text-sm text-muted-foreground">{total} questions in the bank</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="admin-import-csv-btn" onClick={() => setShowImport(true)} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-border text-sm font-medium hover:bg-secondary">
            <Upload className="w-4 h-4" /> CSV Import
          </button>
          <button data-testid="admin-create-mcq-btn" onClick={() => setEditing("new")} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90">
            <Plus className="w-4 h-4" /> New MCQ
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input data-testid="mcq-search" type="text" placeholder="Search question text or explanation…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-card border border-border text-sm" />
        </div>
        <select data-testid="mcq-subject-filter" value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)} className="px-3 py-2.5 rounded-xl bg-card border border-border text-sm">
          <option value="">All subjects</option>
          {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select data-testid="mcq-image-filter" value={hasImage} onChange={(e) => setHasImage(e.target.value)} className="px-3 py-2.5 rounded-xl bg-card border border-border text-sm">
          <option value="">All</option>
          <option value="true">With image</option>
          <option value="false">Text-only</option>
        </select>
      </div>

      <div className="border border-border rounded-2xl overflow-hidden bg-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">Question</th>
              <th className="px-4 py-3 font-medium">Subject</th>
              <th className="px-4 py-3 font-medium">Image</th>
              <th className="px-4 py-3 font-medium">Diff.</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>)}
            {!loading && mcqs.length === 0 && (<tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">No MCQs match this filter</td></tr>)}
            {mcqs.map((m) => (
              <tr key={m.id} className="border-t border-border" data-testid={`admin-mcq-row-${m.id}`}>
                <td className="px-4 py-3 max-w-md">
                  <div className="line-clamp-2">{m.question_text}</div>
                  {m.status === "draft" && <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-warning/15 text-warning">Draft</span>}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{m.subject_id}</td>
                <td className="px-4 py-3">
                  {m.image_url ? (
                    m.audit?.pass === false
                      ? <span className="inline-flex items-center gap-1 text-xs text-destructive"><AlertTriangle className="w-3 h-3" /> failed audit</span>
                      : <span className="inline-flex items-center gap-1 text-xs text-success"><BadgeCheck className="w-3 h-3" /> yes</span>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{m.difficulty}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`edit-mcq-${m.id}`} title="Edit" onClick={() => setEditing(m)} className="p-1.5 rounded hover:bg-secondary"><Edit2 className="w-4 h-4 text-muted-foreground" /></button>
                    <button data-testid={`delete-mcq-${m.id}`} title="Delete" onClick={() => handleDelete(m)} className="p-1.5 rounded hover:bg-destructive/15"><Trash2 className="w-4 h-4 text-destructive" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <MCQEditModal
          mcq={editing === "new" ? blankMCQ : editing}
          subjects={subjects}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {showImport && <CsvImportModal onClose={() => setShowImport(false)} onDone={load} />}
    </div>
  );
};

const MCQEditModal = ({ mcq, subjects, onClose, onSaved }) => {
  const [form, setForm] = useState({ ...mcq });
  const isNew = !mcq.id;
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [generatingImg, setGeneratingImg] = useState(false);
  const [draftTopic, setDraftTopic] = useState("");

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateOption = (idx, txt) => setForm((f) => {
    const opts = [...f.options];
    opts[idx] = { ...opts[idx], text: txt };
    return { ...f, options: opts };
  });

  const draftWithAI = async () => {
    if (!draftTopic.trim()) { toast.error("Enter a topic first"); return; }
    setDrafting(true);
    try {
      const res = await api.post("/admin/mcqs/ai-draft", { topic: draftTopic, subject_id: form.subject_id, difficulty: form.difficulty });
      const d = res.draft;
      setForm({
        ...form,
        clinical_vignette: d.clinical_vignette || "",
        question_text: d.question_text || "",
        options: d.options || form.options,
        correct_option_id: d.correct_option_id || "a",
        explanation: d.explanation || "",
      });
      toast.success("Draft generated — please review before saving");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed");
    } finally { setDrafting(false); }
  };

  const generateImage = async () => {
    if (!form.id) { toast.error("Save the MCQ first, then generate an image"); return; }
    const prompt = window.prompt("Describe the image you want (e.g. 'Periapical radiograph of #36 showing 6mm well-defined radiolucency at apex of mesial root, root canals empty')");
    if (!prompt) return;
    setGeneratingImg(true);
    try {
      const res = await api.post(`/admin/mcqs/${form.id}/image/generate`, { prompt, caption: form.image_caption || null });
      setForm((f) => ({ ...f, image_url: res.image_url }));
      toast.success(res.audit?.pass ? "Image generated & passed audit" : "Image generated but failed audit — regenerate or edit prompt");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image generation failed");
    } finally { setGeneratingImg(false); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !form.id) { toast.error("Save the MCQ first"); return; }
    const fd = new FormData();
    fd.append("file", file);
    if (form.image_caption) fd.append("caption", form.image_caption);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/mcqs/${form.id}/image/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: res.data.image_url }));
      toast.success("Image uploaded");
    } catch (e) { toast.error("Upload failed"); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.question_text.trim()) { toast.error("Question text is required"); return; }
    if (form.options.some((o) => !o.text.trim())) { toast.error("All 4 options must be filled"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post("/admin/mcqs", form);
        toast.success("MCQ created");
        // Allow image-add for the new MCQ — keep modal open
        setForm({ ...form, id: res.mcq.id });
      } else {
        await axios.patch(`${BACKEND_URL}/api/admin/mcqs/${form.id}`, form, { withCredentials: true });
        toast.success("MCQ saved");
        onSaved();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="mcq-edit-modal">
      <div className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-semibold">{isNew ? "New MCQ" : "Edit MCQ"}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-secondary"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={save} className="p-5 space-y-4">
          {/* AI draft helper (only on new) */}
          {isNew && (
            <div className="p-3 rounded-xl bg-primary/5 border border-primary/20">
              <p className="text-xs font-medium mb-2 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Draft with AI</p>
              <div className="flex gap-2">
                <input data-testid="draft-topic-input" type="text" placeholder="Topic, e.g. 'staging of periodontitis 2017'" value={draftTopic} onChange={(e) => setDraftTopic(e.target.value)} className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm" />
                <button type="button" data-testid="draft-topic-go" onClick={draftWithAI} disabled={drafting} className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-50">
                  {drafting ? "Drafting…" : "Draft"}
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Subject</label>
              <select data-testid="mcq-subject" value={form.subject_id} onChange={(e) => updateField("subject_id", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm">
                {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Difficulty (1-5)</label>
              <input data-testid="mcq-difficulty" type="number" min={1} max={5} value={form.difficulty} onChange={(e) => updateField("difficulty", Number(e.target.value))} className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm" />
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Clinical vignette (optional)</label>
            <textarea data-testid="mcq-vignette" rows={2} value={form.clinical_vignette || ""} onChange={(e) => updateField("clinical_vignette", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm" />
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Question</label>
            <textarea data-testid="mcq-question" required rows={2} value={form.question_text} onChange={(e) => updateField("question_text", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm" />
          </div>

          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">Options (tick the correct one)</label>
            {form.options.map((opt, idx) => (
              <div key={opt.id} className="flex items-center gap-2">
                <input type="radio" name="correct" data-testid={`mcq-correct-${opt.id}`} checked={form.correct_option_id === opt.id} onChange={() => updateField("correct_option_id", opt.id)} />
                <span className="text-sm font-mono w-5 text-muted-foreground">{opt.id.toUpperCase()}.</span>
                <input data-testid={`mcq-option-${opt.id}`} type="text" required value={opt.text} onChange={(e) => updateOption(idx, e.target.value)} className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm" />
              </div>
            ))}
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Explanation</label>
            <textarea data-testid="mcq-explanation" rows={3} value={form.explanation} onChange={(e) => updateField("explanation", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm" />
          </div>

          {/* Image section */}
          <div className="p-3 rounded-xl bg-secondary/40 border border-border">
            <p className="text-xs font-medium mb-2 inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Image</p>
            {form.image_url && (
              <img src={`${BACKEND_URL}${form.image_url}`} alt="" className="w-32 h-auto rounded mb-2 border border-border" />
            )}
            <input data-testid="mcq-image-caption" type="text" placeholder="Image caption" value={form.image_caption || ""} onChange={(e) => updateField("image_caption", e.target.value)} className="w-full mb-2 px-3 py-2 rounded-lg bg-background border border-border text-sm" />
            <div className="flex gap-2 flex-wrap">
              <button type="button" data-testid="mcq-gen-image" onClick={generateImage} disabled={generatingImg} className="text-xs px-3 py-1.5 rounded-lg border border-border hover:bg-background flex items-center gap-1 disabled:opacity-50">
                <Sparkles className="w-3 h-3" /> {generatingImg ? "Generating…" : "Generate with AI"}
              </button>
              <label data-testid="mcq-upload-image" className="text-xs px-3 py-1.5 rounded-lg border border-border hover:bg-background cursor-pointer flex items-center gap-1">
                <Upload className="w-3 h-3" /> Upload file
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadImage} className="hidden" />
              </label>
            </div>
            {!form.id && <p className="text-xs text-muted-foreground mt-2">Save the MCQ first to enable image upload/generation.</p>}
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Status</label>
            <select data-testid="mcq-status" value={form.status || "published"} onChange={(e) => updateField("status", e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-sm">
              <option value="published">Published (live to students)</option>
              <option value="draft">Draft (admin only)</option>
            </select>
          </div>

          <div className="flex gap-2 pt-2 border-t border-border">
            <button type="button" onClick={onClose} className="flex-1 px-3 py-2 rounded-lg border border-border text-sm">Cancel</button>
            <button type="submit" data-testid="mcq-save" disabled={saving} className="flex-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-50">
              {saving ? "Saving…" : (isNew ? "Create" : "Save changes")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CsvImportModal = ({ onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setImporting(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/mcqs/bulk-import`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast.success(`Imported ${res.data.inserted} MCQs`);
      onDone();
    } catch { toast.error("Import failed"); }
    finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="csv-import-modal">
      <div className="bg-card rounded-2xl shadow-2xl border border-border w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-semibold">Bulk import MCQs (CSV)</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-secondary"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <p className="text-xs text-muted-foreground">
            Required columns: <span className="font-mono">subject_id, difficulty, clinical_vignette, question_text, option_a, option_b, option_c, option_d, correct, explanation</span>
          </p>
          <input data-testid="csv-file" type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0])} className="w-full text-sm" />
          {result && (
            <div className="text-xs bg-secondary/40 rounded-lg p-3">
              ✓ Inserted: <b>{result.inserted}</b><br />
              {result.skipped?.length > 0 && <>✗ Skipped: <b>{result.skipped.length}</b> rows</>}
            </div>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 px-3 py-2 rounded-lg border border-border text-sm">Close</button>
            <button type="submit" data-testid="csv-import-submit" disabled={!file || importing} className="flex-1 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-50">
              {importing ? "Importing…" : "Import"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminMcqsPage;
